import { Button, Card, Input } from '@brain/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { ExternalLink, KeyRound, Mail } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { refreshSession } from '../lib/api.ts'
import { authClient } from '../lib/auth.ts'
import { isAuthorizeUrl } from '../lib/mcp.ts'
import { apiUrl, isTauri } from '../lib/platform.ts'
import { safeRedirect } from '../lib/redirect.ts'

export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>): { redirect?: string; error?: string } => ({
    ...(typeof s.redirect === 'string' ? { redirect: s.redirect } : {}),
    ...(typeof s.error === 'string' ? { error: s.error } : {}),
  }),
  component: Login,
})

interface AuthConfig {
  passkey: boolean
  google: boolean
  microsoft: boolean
  magicLink: boolean
}

type Status = { kind: 'idle' } | { kind: 'busy' } | { kind: 'sent' } | { kind: 'error'; message: string }

function Login() {
  const { t } = useTranslation()
  const router = useRouter()
  const queryClient = useQueryClient()
  const search = Route.useSearch()
  const target = safeRedirect(search.redirect)
  const [email, setEmail] = useState('')
  // Which sign-in methods the server offers (e-mail links need SMTP, social logins need OAuth clients)
  const config = useQuery({
    queryKey: ['auth-config'],
    queryFn: async (): Promise<AuthConfig> => (await fetch(apiUrl('/api/auth-config'))).json(),
    staleTime: 5 * 60_000,
  }).data
  const [status, setStatus] = useState<Status>(
    search.error === 'desktop' ? { kind: 'error', message: t('login.errorDesktop') } : { kind: 'idle' },
  )

  async function done() {
    await refreshSession(queryClient)
    // MCP client connecting (ADR 0009): continue the OAuth authorize request on the server
    if (isAuthorizeUrl(target)) return window.location.assign(target)
    router.history.push(target)
  }

  async function passkey() {
    setStatus({ kind: 'busy' })
    const res = await authClient.signIn.passkey()
    if (res?.error) return setStatus({ kind: 'error', message: t('login.errorPasskey') })
    await done()
  }

  async function social(provider: 'google' | 'microsoft') {
    setStatus({ kind: 'busy' })
    const res = await authClient.signIn.social({ provider, callbackURL: target }).catch(() => null)
    if (!res || res.error) setStatus({ kind: 'error', message: t('login.errorSocial') })
  }

  async function magicLink(e: FormEvent) {
    e.preventDefault()
    setStatus({ kind: 'busy' })
    // Fresh magic-link sign-ins land in the settings to add a passkey, unless a redirect was requested.
    const callbackURL = search.redirect ? target : '/einstellungen?passkey=hinzufuegen'
    const res = await authClient.signIn.magicLink({ email, callbackURL })
    setStatus(res.error ? { kind: 'error', message: t('login.errorLink') } : { kind: 'sent' })
  }

  async function browser() {
    // Passkeys and OAuth redirects don't work in the ad-hoc signed WebView; log in via the system browser.
    const { openInBrowser } = await import('../lib/tauri.ts')
    await openInBrowser(apiUrl('/desktop-login'))
    setStatus({ kind: 'idle' })
  }

  const busy = status.kind === 'busy'

  return (
    <div className="flex min-h-dvh items-center justify-center bg-page px-4 py-10">
      <div data-tauri-drag-region className="fixed inset-x-0 top-0 h-8" />
      <Card className="w-full max-w-sm p-8">
        <div className="mb-6 flex size-11 items-center justify-center rounded-tile bg-ink font-medium text-[16px] text-white">
          {t('app.short')}
        </div>
        <h1 className="text-[22px] text-text-1">{t('login.title')}</h1>
        <p className="mt-1 text-[14px] text-text-2">{t('login.subtitle')}</p>

        {isTauri ? (
          <div className="mt-6 flex flex-col gap-2">
            <Button variant="primary" size={40} onClick={browser}>
              <ExternalLink size={18} strokeWidth={1.9} />
              {t('login.browser')}
            </Button>
            <p className="mt-2 text-[12px] text-meta leading-relaxed">{t('login.browserHint')}</p>
          </div>
        ) : (
          <>
            <div className="mt-6 flex flex-col gap-2">
              <Button variant="primary" size={40} onClick={passkey} disabled={busy}>
                <KeyRound size={18} strokeWidth={1.9} />
                {t('login.passkey')}
              </Button>
              <Button size={40} onClick={() => social('google')} disabled={busy}>
                {t('login.google')}
              </Button>
              {config?.microsoft && (
                <Button size={40} onClick={() => social('microsoft')} disabled={busy}>
                  {t('login.microsoft')}
                </Button>
              )}
            </div>

            {config?.magicLink && (
              <>
                <p className="mt-6 mb-2 meta-label">{t('login.or')}</p>
                <form onSubmit={magicLink} className="flex flex-col gap-2">
                  <label htmlFor="email" className="sr-only">
                    {t('login.email')}
                  </label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username webauthn"
                    required
                    value={email}
                    placeholder={t('login.emailPlaceholder')}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button type="submit" size={40} disabled={busy || !email}>
                    <Mail size={18} strokeWidth={1.9} />
                    {t('login.sendLink')}
                  </Button>
                </form>
              </>
            )}
          </>
        )}

        <div aria-live="polite" className="mt-4 min-h-5 text-[13px]">
          {status.kind === 'sent' && <p className="text-green-fg">{t('login.linkSent')}</p>}
          {status.kind === 'error' && (
            <p role="alert" className="text-apricot-fg">
              {status.message}
            </p>
          )}
        </div>
        {!isTauri && (
          <p className="mt-2 text-[12px] text-meta leading-relaxed">
            {t(config?.magicLink ? 'login.hint' : 'login.hintNoLink')}
          </p>
        )}
      </Card>
    </div>
  )
}
